/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { changeBottomNavigationElements, changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { changeTool } from "../slices/footer/footerSlice";
import jsonEquals from "../functions/jsonEquals";
import updateElement from "../functions/updateElement";
import ReferenceEntry, { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { displayStringFromFHIRIdentifier } from "../datatypeComponents/IdentifierEntry";
import CodeableConceptEntry, { displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import AttachmentEntry, { DisplayFromFHIRAttachment } from "../datatypeComponents/AttachmentEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import CodeEntry from "../datatypeComponents/CodeEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import Spinner from "../components/Spinner";

const documentReferenceTypeValueSet = [
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "statistical-analysis-plan",
            "display": "Statistical analysis plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "data-management-plan",
            "display": "Data management plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "interim-analysis-plan",
            "display": "Interim analysis plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "adverse-event-monitoring-plan",
            "display": "Adverse event monitoring plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "auditing-plan",
            "display": "Auditing plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "irb-approval",
            "display": "IRB approval"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "dissemination-plan",
            "display": "Dissemination plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "consent-form",
            "display": "Consent form"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "consent-plan",
            "display": "Consent plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "confidential-personal-data-management-plan",
            "display": "Confidential personal data management plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "declaration-of-interests",
            "display": "Declaration of interests"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "data-access-plan",
            "display": "Data access plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "ancillary-care-plan",
            "display": "Ancillary care plan"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "dataset",
            "display": "Dataset"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "dataset-access",
            "display": "Dataset access"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "software",
            "display": "Software"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "software-access",
            "display": "Software access"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "education",
            "display": "Education"
        }
        ]
    },
    {
        "coding": [{
            "system": "http://hl7.org/fhir/uv/ebm/CodeSystem/research-study-document-types-code-system",
            "code": "specimen-plan",
            "display": "Specimen plan"
        }
        ]
    }
];

const DocumentReferenceContentEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
    startCollapsed }) => {

    const [documentReferenceContentState, setDocumentReferenceContentState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingDocumentReferenceContent = {
            "attachment": ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingDocumentReferenceContent.extension = startingValue.extension; }
            if (startingValue.attachment) { startingDocumentReferenceContent.attachment = startingValue.attachment; }
            if (startingValue.profile) { startingDocumentReferenceContent.profile = startingValue.profile; }
        }
        setDocumentReferenceContentState(JSON.parse(JSON.stringify(startingDocumentReferenceContent)));
    }, []);

    useEffect((() => {
        if (Object.keys(documentReferenceContentState).length > 0) {
            let newDocumentReferenceContent = {};
            if (documentReferenceContentState.extension && Object.keys(documentReferenceContentState.extension).length) {
                newDocumentReferenceContent.extension = documentReferenceContentState.extension;
            }
            if (documentReferenceContentState.attachment && Object.keys(documentReferenceContentState.attachment).length) {
                newDocumentReferenceContent.attachment = documentReferenceContentState.attachment;
            }
            if (documentReferenceContentState.profile && Object.keys(documentReferenceContentState.profile).length) {
                newDocumentReferenceContent.profile = documentReferenceContentState.profile;
            }

            if (Object.keys(newDocumentReferenceContent).length === 0) {
                newDocumentReferenceContent = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newDocumentReferenceContent } });
        }
    }), [documentReferenceContentState]);

    if (startCollapsedState) {
        return <>
            <div>
                <b>{fieldLabel}: </b>
                {startingValue && <DisplayFromFHIRAttachment attachment={startingValue.attachment} />}
                &nbsp;&nbsp;
                <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
            </div>
        </>
    } else if (Object.keys(documentReferenceContentState).length > 0) {
        return <>{documentReferenceContentState.attachment !== undefined &&
            <>
                <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
                <div style={{ marginLeft: "24px" }}>
                    <AttachmentEntry elementName='attachment' setResourceState={setDocumentReferenceContentState}
                        startCollapsed={documentReferenceContentState.attachment !== ""}
                        fieldLabel={'Attachment for ' + fieldLabel}
                        startingValue={documentReferenceContentState.attachment} />
                </div>
            </>}
        </>
    }
}

const DocumentReferenceDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    let displayTitle = resourceJson.content?.[0]?.attachment?.title ||
        parentResourceDetail?.displayTitle || "Title missing";

    const [documentReferenceResourceState, setDocumentReferenceResourceState] = useState({});
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Content", "Description", "Identification", "Subject", "Stewardship"]));
        dispatch(changeBottomNavigationElements(["JSON Outline"]));
        dispatch(changeBrowserTabTitle("FEvIR DocumentReference - " + displayTitle));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setDocumentReferenceResourceState(olderVersionJson);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            let resourceJsonCopy = JSON.parse(JSON.stringify(resourceJson));
            if (!resourceJsonCopy.content || !Array.isArray(resourceJsonCopy.content) || resourceJsonCopy.content.length === 0) {
                resourceJsonCopy.content = [{ "attachment": "" }];
            }
            setDocumentReferenceResourceState(resourceJsonCopy);
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: DocumentReference " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect(() => {
        if (editMode && resourceJson && Array.isArray(documentReferenceResourceState.content) &&
            documentReferenceResourceState.content.length > 0) {
            let previousInitialValue = parentResourceDetail.resourceJson.content;
            if (documentReferenceResourceState.content !== undefined &&
                !jsonEquals(parentResourceDetail.revisedResourceJson.content, documentReferenceResourceState.content) &&
                !(!previousInitialValue &&
                    Array.isArray(documentReferenceResourceState.content) &&
                    (documentReferenceResourceState.content.length === 0 ||
                        (documentReferenceResourceState.content.length === 1 && !documentReferenceResourceState.content[0])
                    )
                )
            ) {
                console.log("resourceUpdate for DocumentReference.content");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                newResourceDetail.revisedResourceJson.content = documentReferenceResourceState.content;
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [documentReferenceResourceState.content]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "type", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.type]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "status", "code", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.status]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "docStatus", "code", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.docStatus]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "description", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "category", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.category]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "identifier", "ArrayOfIdentifier", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.identifier]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "version", "string", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.version]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "subject", "Reference", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.subject]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "related", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.related]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "author", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.author]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(documentReferenceResourceState, "custodian", "Reference", parentResourceDetail,
                dispatch);
        }
    }, [documentReferenceResourceState.custodian]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {documentReferenceResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="content">Content</h3>
                <div style={{ marginLeft: "24px" }}>
                    {displayTitle && <p><b>Title: </b>{displayTitle}</p>}
                    {editMode ?
                        <>
                            <CodeableConceptEntry elementName='type' fieldLabel='Type'
                                startingValue={documentReferenceResourceState.type}
                                codeableConceptLevelValueSet={documentReferenceTypeValueSet}
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {documentReferenceResourceState.type && <p><b>Type: </b>{displayStringFromFHIRCodeableConcept(documentReferenceResourceState.type)}</p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <ArrayEntry datatype="DocumentReferenceContent" elementName='content'
                                fieldLabel='Content (as Attachment)'
                                startingValue={documentReferenceResourceState.content || []}
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {(Array.isArray(documentReferenceResourceState.content) &&
                                documentReferenceResourceState.content.length) && documentReferenceResourceState.content.map((content, index) => {
                                    return <div key={index}>
                                        <DisplayFromFHIRAttachment attachment={content.attachment} />
                                    </div>
                                })}
                        </>
                    }
                    {editMode ?
                        <>
                            <CodeEntry elementName='status' fieldLabel='Resource status'
                                startingValue={documentReferenceResourceState.status || 'current'}
                                allowedValues={['current', 'superseded', 'entered-in-error']}
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {documentReferenceResourceState.status && <p><b>Resource status: </b>{documentReferenceResourceState.status}</p>}
                        </>
                    }
                    {editMode ?
                        <>
                            <CodeEntry elementName='docStatus' fieldLabel='Document status'
                                startingValue={documentReferenceResourceState.docStatus}
                                allowedValues={['registered', 'partial', 'preliminary', 'final', 'amended', 'corrected', 'appended', 'cancelled', 'entered-in-error', 'deprecated', 'unknown']}
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {documentReferenceResourceState.docStatus && <p><b>Document status: </b>{documentReferenceResourceState.docStatus}</p>}
                        </>
                    }
                </div>
                <h3 id="description">Description</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='description' fieldLabel='Description'
                                startingValue={documentReferenceResourceState.description} startCollapsed
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {documentReferenceResourceState.description &&
                                <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={documentReferenceResourceState.description} /></div>}
                        </>
                    }
                    {editMode ?
                        <>
                            <ArrayEntry datatype="CodeableConcept" elementName='category'
                                fieldLabel='Category' startCollapsed
                                startingValue={documentReferenceResourceState.category || []}
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {(documentReferenceResourceState.category?.length > 1) && documentReferenceResourceState.category.map((category, identifierIndex) => {
                                return <div key={identifierIndex}>
                                    <p><b>Category: </b>{displayStringFromFHIRCodeableConcept(category)}</p>
                                </div>
                            })}
                        </>
                    }
                </div>
                <h3 id="identification">Identification</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <ArrayEntry datatype="Identifier" elementName='identifier'
                                fieldLabel='Identifier' startCollapsed
                                startingValue={documentReferenceResourceState.identifier || []}
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {(documentReferenceResourceState.identifier?.length > 1) &&
                                documentReferenceResourceState.identifier.map((identifier, identifierIndex) => {
                                    return <div key={identifierIndex}>
                                        <p><b>Identifier: </b>{displayStringFromFHIRIdentifier(identifier)}</p>
                                    </div>
                                })}
                        </>
                    }
                    {editMode ?
                        <>
                            <StringEntry elementName='version' fieldLabel='Version'
                                startingValue={documentReferenceResourceState.version}
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {documentReferenceResourceState.version && <p><b>Version: </b>{documentReferenceResourceState.version}</p>}
                        </>
                    }
                </div>
                <h3 id="subject">Subject</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <ReferenceEntry elementName='subject'
                                fieldLabel='Subject'
                                startingValue={documentReferenceResourceState.subject || null}
                                enableCreation={true} startCollapsed
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {documentReferenceResourceState.subject &&
                                <div><b>Subject: </b><DisplayFromFHIRReference reference={documentReferenceResourceState.subject}
                                    containedResourceJson={documentReferenceResourceState.subject.reference && containedResourceDictionary[documentReferenceResourceState.subject.reference]} /></div>}
                        </>
                    }
                    {editMode ?
                        <>
                            <ArrayEntry datatype="Reference" elementName='related'
                                fieldLabel='Related Item'
                                startingValue={documentReferenceResourceState.related || []}
                                enableCreation={true} startCollapsed
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {(documentReferenceResourceState.related?.length > 1) && documentReferenceResourceState.related.map((related, relatedIndex) => {
                                return <div key={relatedIndex}>
                                    <div><b>Related Item: </b><DisplayFromFHIRReference reference={related}
                                        containedResourceJson={related?.reference && containedResourceDictionary[related.reference]} /></div>
                                </div>
                            })}
                        </>
                    }
                </div>
                <h3 id="stewardship">Stewardship</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <ArrayEntry datatype="Reference" elementName='author'
                                fieldLabel='Author'
                                referencedResourceTypes={["Practitioner", "PractitionerRole", "Organization", "Device", "Patient", "RelatedPerson", "CareTeam", "Group"]}
                                startingValue={documentReferenceResourceState.author || []}
                                setResourceState={setDocumentReferenceResourceState}
                                enableCreation={true} startCollapsed />
                        </>
                        :
                        <>
                            {(documentReferenceResourceState.author?.length > 1) && documentReferenceResourceState.author.map((author, authorIndex) => {
                                return <div key={authorIndex}>
                                    <div><b>Author: </b><DisplayFromFHIRReference reference={author}
                                        containedResourceJson={author?.reference && containedResourceDictionary[author.reference]} /></div>
                                </div>
                            })}
                        </>
                    }
                    {editMode ?
                        <>
                            <ReferenceEntry elementName='custodian'
                                containedResourceDictionary={containedResourceDictionary}
                                fieldLabel='Custodian' startingResourceType="Organization"
                                enableCreation={true} startCollapsed
                                startingValue={documentReferenceResourceState.custodian || null}
                                setResourceState={setDocumentReferenceResourceState} />
                        </>
                        :
                        <>
                            {documentReferenceResourceState.custodian &&
                                <div><b>Custodian: </b><DisplayFromFHIRReference reference={documentReferenceResourceState.custodian}
                                    containedResourceJson={documentReferenceResourceState.custodian.reference && containedResourceDictionary[documentReferenceResourceState.custodian.reference]} /></div>}
                        </>
                    }
                </div>
            </div>
        </>}
    </div>
}

export default DocumentReferenceDisplay;
export { DocumentReferenceContentEntry };